/**
 * User: hella
 * Date: 4/6/14
 * Time: 1:15 PM
 * Copyright 1stdibs.com, Inc. 2014. All Rights Reserved.
 *
 * formerly:
 * js/global/core/dibs.js#baseDomain
 * js/global/core/dibs.js#parseUri
 * js/global/core/dibs.js#URL
 * dibs.baseDomain
 * dibs.parseUri
 * dibs.URL
 */
'use strict';

const http = require('./http.es');
const qs = require('qs');

/**
 * @param {string} url
 * @private
 */
function throwError(url) {
    throw new Error(`options.url required. \`${url}\` with typeof \`${typeof url}\` was passed in`);
}

/**
 * Returns a query param stripped of delimiters.
 * @private
 * @returns {*}
 */
function stripDelimiters(param) {
    const delimiters = /[\+-]/g;
    const replacement = ' ';
    return param.replace(delimiters, replacement);
}

/**
 * Parse the url path into an array
 * @param {string} path
 * @private
 * @returns {Array}
 */
function parsePathSegments(path) {
    const segments = path.split('/');
    while (segments.indexOf('') !== -1) {
        segments.splice(segments.indexOf(''), 1);
    }
    return segments;
}

/**
 * Remove any blacklisted params
 * @param {object} queryParams Key/value pairs of query params
 * @param {Array} blacklistParams Array of query params to remove
 * @private
 */
function removeRestrictedParams(queryParams, blacklistParams) {
    // do not modify original object
    const params = Object.assign({}, queryParams);
    Object.keys(params).forEach(param => {
        // if the param is in the blacklist,
        // delete it from the params object
        if (Array.isArray(blacklistParams) && blacklistParams.indexOf(param) > -1) {
            delete params[param];
        }
    });
    return exports.buildQueryString(params);
}

/**
 * Create a clean url that can be used as a value of a query parameter
 * ie. /?modal=login&redirect=%2ffavorites%2fitems%2f
 * @param {object} options
 * @param {string} options.url The url to base everything off of
 * @param {Array} [options.blacklistParams] Array of blacklisted query params to remove
 * @covers removeRestrictedParams
 * @covers throwError
 * @returns {string}
 */
exports.createCleanUrl = function (options) {
    options = options || {};
    const url = options.url;
    if (!url || typeof url !== 'string') {
        throwError(url);
    }

    const blacklist = options.blacklistParams || [];
    const urlParts = exports.parseUri(url);
    const queryString = urlParts.queryKey;
    const cleanQueryString = removeRestrictedParams(queryString, blacklist);

    const toPush = [
        urlParts.protocol ? `${urlParts.protocol}://` : '',
        // only add `host` if `protocol` is present
        urlParts.protocol ? urlParts.host : '',
        urlParts.path,
        cleanQueryString
    ];
    const parts = toPush.reduce((accum, value) => {
        if (value) {
            accum.push(value);
        }
        return accum;
    }, []);

    return parts.join('');
};

/**
 *
 * @param {string} str The url to parse
 * @param {object} [parseOptions] Options to parse the url parts against
 * @param {boolean} [parseOptions.mode="loose"] Regex to use when parsing the url
 * @param {boolean} [parseOptions.decode=false] Decode the query param value
 * @covers stripDelimiters
 * @covers parsePathSegments
 * @returns {{}} Returns an object of properties in the `options.key` property
 */
exports.parseUri = function parseUri(str, parseOptions) {
    parseOptions = parseOptions || {};
    const uri = {};
    const options = {
        key: ["source", "protocol", "authority", "userInfo", "user", "password", "host", "port", "relative", "path", "directory", "file", "query", "anchor"],
        q: {
            name: "queryKey",
            parser: /(?:^|&)([^&=]*)=?([^&]*)/g
        },
        parser: {
            strict: /^(?:([^:\/?#]+):)?(?:\/\/((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?))?((((?:[^?#\/]*\/)*)([^?#]*))(?:\?([^#]*))?(?:#(.*))?)/,
            loose: /^(?:(?![^:@]+:[^:@\/]*@)([^:\/?#.]+):)?(?:\/\/)?((?:(([^:@]*)(?::([^:@]*))?)?@)?([^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/
        }
    };
    const m = options.parser[parseOptions.mode || 'loose'].exec(str);
    let i = options.key.length;

    while (i--) {
        uri[options.key[i]] = m[i] || "";
    }

    uri[options.q.name] = {};
    // options.key[12] = options.key['query']
    //
    /**
     * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/replace#Specifying_a_function_as_a_parameter
     * @param {string} $0 is the substring match for that iteration
     * @param {string} $1 is the 1st capturing group (the param name)
     * @param {string} $2 is the second group (the param value)
     */
    uri[options.key[12]].replace(options.q.parser, ($0, $1, $2) => {
        const params = uri[options.q.name];
        let newVal = parseOptions.decode ? decodeURIComponent($2) : $2;

        if (parseOptions.stripDelimiters) {
            newVal = stripDelimiters(newVal);
        }

        if ($1) {
            // store values as arrays so that the same param can be used to specify multiple values
            if (parseOptions.toArrays) {
                // if there is already a value, make sure it's an array
                if (params.hasOwnProperty($1)) {
                    if (!Array.isArray(params[$1])) {
                        params[$1] = [params[$1]];
                    }

                    // add new value
                    params[$1].push(newVal);

                } else {
                    params[$1] = newVal; // no value yet, set as single value
                }
            } else {
                params[$1] = newVal; // otherwise just set value, even if overriding previous value
            }
        }
    });

    uri.pathSegments = parsePathSegments(uri.path);

    return uri;
};

/**
 * Returns you a query string from params set.
 * @param {object} params
 * @returns {string} Either the query param string with `?` prepend or an empty string
 */
exports.buildQueryString = function (params) {
    const result = [];
    Object.keys(params).forEach(key => {
        const vals = params[key];
        if (Array.isArray(vals)) {
            // do not reassign `result` here
            vals.reduce((accum, val) => {
                accum.push(`${key}=${encodeURIComponent(val)}`);
                // can't return while doing push otherwise
                // returns numbers, not the array
                return accum;
            }, result);
        } else if (typeof vals !== 'undefined') {
            // just a single value, add to url
            result.push(`${key}=${encodeURIComponent(vals)}`);
        }
    });

    return result.length ? '?' + result.join('&') : '';
};

// make the parsed URL of the current page available on the global dibs namespace
// handy to have around
exports.URL = exports.parseUri(global.location);

/**
 * use http#redirect
 * @deprecated
 */
exports.redirect = function (options) {
    console.warn('this method is deprecated. use http#redirect instead');
    http.redirect(options);
};

/**
 * Returns you a url with specified query params removed
 * @param {string} url is the full url with query params
 * @param {array|string} paramKeysToRemove is an array or string of query param keys to be removed from url
 * @returns {string} new url
 */
exports.removeQueryParams = (url, paramKeysToRemove) => {
    const urlSplit = url.split('?');
    const basePath = urlSplit[0];
    const queryString = urlSplit[1];
    const qsObject = qs.parse(queryString);

    if (!Array.isArray(paramKeysToRemove)) {
        paramKeysToRemove = [paramKeysToRemove];
    }

    paramKeysToRemove.forEach(paramKey => qsObject[paramKey] = undefined);
    const qsString = qs.stringify(qsObject, { encode: false });

    return qsString ? basePath.concat('?', qsString) : basePath;
};



// WEBPACK FOOTER //
// ./~/bunsen/helpers/uri.es.js